import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import "./vehicle-stock.css";

type RoleKey = "stand_master" | "manager" | "sales" | "operations" | "user";
type Vehicle = {
  id: string;
  organization_id: string;
  stock_number: string;
  make: string;
  model: string;
  version: string | null;
  registration: string | null;
  first_registration_year: number | null;
  fuel_type: string | null;
  transmission: string | null;
  mileage_km: number;
  color: string | null;
  purchase_price_cents: number | null;
  sale_price_cents: number | null;
  currency_code: string;
  stock_status: string;
  operational_stage: string;
  acquisition_type: string;
  publication_status: string;
  sold_publication_mode: string;
  pause_reason: string | null;
  featured: boolean;
  description: string | null;
  updated_at: string;
};
type ConsignmentOwner = {
  id: string;
  display_name: string;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
};
type ConsignmentAgreement = {
  id: string;
  owner_id: string;
  status: string;
  owner_amount_cents: number;
  commission_type: string;
  commission_value: number | null;
  minimum_sale_price_cents: number | null;
  starts_on: string;
  ends_on: string | null;
  terms: string | null;
  internal_notes: string | null;
};
const stockLabels: Record<string, string> = {
  in_stock: "Em stock",
  reserved: "Reservada",
  sale_in_progress: "Em venda",
  sold: "Vendida",
  delivery: "A entregar",
  delivered: "Entregue",
  paused: "Em pausa",
  archived: "Arquivada",
};
const stageLabels: Record<string, string> = {
  purchase: "Compra",
  transport: "Transporte",
  reception: "Receção",
  checkup: "Verificação",
  preparation: "Preparação",
  workshop: "Oficina",
  inspection: "Inspeção",
  exposure: "Exposição",
};
const acquisitionLabels: Record<string, string> = {
  owned: "Viatura própria",
  consignment: "À consignação",
  trade_in: "Retoma",
};
const publicationLabels: Record<string, string> = {
  draft: "Rascunho",
  published: "Publicada",
  unpublished: "Não publicada",
};
const saleStatuses = new Set([
  "sale_in_progress",
  "sold",
  "delivery",
  "delivered",
]);
const money = (value: number | null, currency: string) =>
  value == null
    ? "—"
    : new Intl.NumberFormat(currency === "BRL" ? "pt-BR" : "pt-PT", {
        style: "currency",
        currency,
      }).format(value / 100);

export function VehicleStock({
  organizationId,
  currencyCode,
  writable,
  roleKey,
}: {
  organizationId: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
}) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [loading, setLoading] = useState(true),
    [selected, setSelected] = useState<Vehicle | null>(null),
    [creating, setCreating] = useState(false),
    [status, setStatus] = useState("all"),
    [search, setSearch] = useState(""),
    [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("vehicles")
      .select("*")
      .eq("organization_id", organizationId)
      .order("updated_at", { ascending: false });
    if (error) setMessage(error.message);
    setVehicles((data ?? []) as Vehicle[]);
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message]);
  const filtered = useMemo(
    () =>
      vehicles.filter(
        (v) =>
          (status === "all" || v.stock_status === status) &&
          `${v.make} ${v.model} ${v.version ?? ""} ${v.registration ?? ""} ${v.stock_number}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [vehicles, status, search],
  );
  const counts = useMemo(
    () => ({
      total: vehicles.filter((v) => v.stock_status !== "archived").length,
      published: vehicles.filter((v) => v.publication_status === "published")
        .length,
      preparation: vehicles.filter((v) =>
        ["preparation", "workshop", "inspection"].includes(v.operational_stage),
      ).length,
      sold: vehicles.filter((v) =>
        ["sold", "delivery", "delivered"].includes(v.stock_status),
      ).length,
    }),
    [vehicles],
  );
  return (
    <section className="vehicle-stock">
      <header className="stock-heading">
        <div>
          <p className="eyebrow">Gestão de viaturas</p>
          <h2>Stock</h2>
          <p>
            Estado comercial, etapa operacional e publicação são controlados
            separadamente.
          </p>
        </div>
        {writable && (
          <button className="stock-primary" onClick={() => setCreating(true)}>
            + Adicionar viatura
          </button>
        )}
      </header>
      {message && (
        <button className="stock-notice" onClick={() => setMessage(null)}>
          {message} ×
        </button>
      )}
      <div className="stock-summary">
        <article>
          <small>Stock atual</small>
          <strong>{counts.total}</strong>
        </article>
        <article>
          <small>Publicadas</small>
          <strong>{counts.published}</strong>
        </article>
        <article>
          <small>Em preparação</small>
          <strong>{counts.preparation}</strong>
        </article>
        <article>
          <small>Vendidas</small>
          <strong>{counts.sold}</strong>
        </article>
      </div>
      <div className="stock-filters">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar viatura, matrícula ou referência…"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Todos os estados</option>
          {Object.entries(stockLabels).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <div className="stock-empty">A carregar o stock…</div>
      ) : filtered.length === 0 ? (
        <div className="stock-empty">
          <strong>Não existem viaturas para estes filtros.</strong>
          <span>
            {writable
              ? "Adicione uma viatura ou altere os filtros."
              : "O stand ainda não registou viaturas."}
          </span>
        </div>
      ) : (
        <div className="vehicle-list">
          {filtered.map((v) => (
            <button
              key={v.id}
              className="vehicle-row"
              onClick={() => setSelected(v)}
            >
              <span className="vehicle-ref">{v.stock_number}</span>
              <span className="vehicle-name">
                <strong>
                  {v.make} {v.model}
                </strong>
                <small>
                  {acquisitionLabels[v.acquisition_type]} ·{" "}
                  {stageLabels[v.operational_stage]}
                </small>
              </span>
              <span>
                <strong>{v.first_registration_year ?? "—"}</strong>
                <small>{v.registration ?? "Sem matrícula"}</small>
              </span>
              <span>
                <strong>{v.mileage_km.toLocaleString("pt-PT")} km</strong>
                <small>{v.fuel_type ?? "Combustível não indicado"}</small>
              </span>
              <span className={`stock-status status-${v.stock_status}`}>
                {stockLabels[v.stock_status]}
              </span>
              <span>
                <strong>{money(v.sale_price_cents, v.currency_code)}</strong>
                <small>{publicationLabels[v.publication_status]}</small>
              </span>
            </button>
          ))}
        </div>
      )}
      {(creating || selected) && (
        <VehicleDialog
          vehicle={selected}
          organizationId={organizationId}
          currencyCode={currencyCode}
          writable={writable}
          roleKey={roleKey}
          onClose={() => {
            setCreating(false);
            setSelected(null);
          }}
          onSaved={async () => {
            const edited = Boolean(selected);
            setCreating(false);
            setSelected(null);
            setMessage(
              edited ? "Viatura atualizada." : "Viatura adicionada ao stock.",
            );
            await load();
          }}
        />
      )}
    </section>
  );
}

function VehicleDialog({
  vehicle,
  organizationId,
  currencyCode,
  writable,
  roleKey,
  onClose,
  onSaved,
}: {
  vehicle: Vehicle | null;
  organizationId: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [chosenStatus, setChosenStatus] = useState(
      vehicle?.stock_status ?? "in_stock",
    );
  const [acquisitionType, setAcquisitionType] = useState(
    vehicle?.acquisition_type ?? "owned",
  );
  const [owners, setOwners] = useState<ConsignmentOwner[]>([]);
  const [ownerId, setOwnerId] = useState("__new__");
  const [agreement, setAgreement] = useState<ConsignmentAgreement | null>(null);
  const [consignmentReady, setConsignmentReady] = useState(!vehicle);
  const canManageLifecycle =
    roleKey === "stand_master" || roleKey === "manager";
  useEffect(() => {
    void Promise.all([
      supabase
        .from("consignment_owners")
        .select("id,display_name,tax_id,email,phone")
        .eq("organization_id", organizationId)
        .eq("active", true)
        .order("display_name"),
      vehicle
        ? supabase
            .from("vehicle_consignment_agreements")
            .select(
              "id,owner_id,status,owner_amount_cents,commission_type,commission_value,minimum_sale_price_cents,starts_on,ends_on,terms,internal_notes",
            )
            .eq("vehicle_id", vehicle.id)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null }),
    ]).then(([ownerResult, agreementResult]) => {
      if (ownerResult.error) setError(ownerResult.error.message);
      setOwners((ownerResult.data ?? []) as ConsignmentOwner[]);
      const current = agreementResult.data as ConsignmentAgreement | null;
      setAgreement(current);
      if (current) setOwnerId(current.owner_id);
      setConsignmentReady(true);
    });
  }, [organizationId, vehicle]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!writable) return;
    setSaving(true);
    setError(null);
    const f = new FormData(event.currentTarget);
    const payload = {
      organization_id: organizationId,
      stock_number: String(f.get("stock_number") ?? "").trim(),
      make: String(f.get("make") ?? "").trim(),
      model: String(f.get("model") ?? "").trim(),
      version: String(f.get("version") || "") || null,
      registration: String(f.get("registration") || "").toUpperCase() || null,
      first_registration_year: f.get("year") ? Number(f.get("year")) : null,
      fuel_type: String(f.get("fuel_type") || "") || null,
      transmission: String(f.get("transmission") || "") || null,
      mileage_km: Number(f.get("mileage") || 0),
      color: String(f.get("color") || "") || null,
      purchase_price_cents: f.get("purchase_price")
        ? Math.round(Number(f.get("purchase_price")) * 100)
        : null,
      sale_price_cents: f.get("sale_price")
        ? Math.round(Number(f.get("sale_price")) * 100)
        : null,
      currency_code: currencyCode,
      acquisition_type: acquisitionType,
      operational_stage: String(f.get("operational_stage")),
      stock_status: String(f.get("stock_status")),
      pause_reason: String(f.get("pause_reason") || "") || null,
      publication_status: String(f.get("publication_status")),
      sold_publication_mode: String(f.get("sold_publication_mode")),
      featured: f.get("featured") === "on",
      description: String(f.get("description") || "") || null,
    };
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const vehicleResult = vehicle
      ? await supabase
          .from("vehicles")
          .update(payload)
          .eq("id", vehicle.id)
          .eq("organization_id", organizationId)
          .select("id")
          .single()
      : await supabase
          .from("vehicles")
          .insert({ ...payload, created_by: user?.id })
          .select("id")
          .single();
    if (vehicleResult.error) {
      setSaving(false);
      setError(vehicleResult.error.message);
      return;
    }
    const vehicleId = vehicleResult.data.id as string;
    if (canManageLifecycle && acquisitionType === "consignment") {
      let selectedOwnerId = ownerId;
      if (selectedOwnerId === "__new__") {
        const ownerResult = await supabase
          .from("consignment_owners")
          .insert({
            organization_id: organizationId,
            owner_type: String(f.get("owner_type")),
            display_name: String(f.get("owner_name") ?? "").trim(),
            tax_id: String(f.get("owner_tax_id") || "") || null,
            email: String(f.get("owner_email") || "") || null,
            phone: String(f.get("owner_phone") || "") || null,
            address: String(f.get("owner_address") || "") || null,
            notes: String(f.get("owner_notes") || "") || null,
            created_by: user?.id,
          })
          .select("id")
          .single();
        if (ownerResult.error) {
          setSaving(false);
          setError(
            `A viatura foi guardada, mas o proprietário não: ${ownerResult.error.message}`,
          );
          return;
        }
        selectedOwnerId = ownerResult.data.id;
      }
      const agreementPayload = {
        organization_id: organizationId,
        vehicle_id: vehicleId,
        owner_id: selectedOwnerId,
        status: String(f.get("agreement_status")),
        owner_amount_cents: Math.round(
          Number(f.get("owner_amount") || 0) * 100,
        ),
        commission_type: String(f.get("commission_type")),
        commission_value: f.get("commission_value")
          ? Number(f.get("commission_value"))
          : null,
        minimum_sale_price_cents: f.get("minimum_sale_price")
          ? Math.round(Number(f.get("minimum_sale_price")) * 100)
          : null,
        currency_code: currencyCode,
        starts_on: String(f.get("starts_on")),
        ends_on: String(f.get("ends_on") || "") || null,
        terms: String(f.get("terms") || "") || null,
        internal_notes: String(f.get("consignment_notes") || "") || null,
        created_by: user?.id,
      };
      const agreementResult = agreement
        ? await supabase
            .from("vehicle_consignment_agreements")
            .update(agreementPayload)
            .eq("id", agreement.id)
        : await supabase
            .from("vehicle_consignment_agreements")
            .insert(agreementPayload);
      if (agreementResult.error) {
        setSaving(false);
        setError(
          `A viatura foi guardada, mas o acordo não: ${agreementResult.error.message}`,
        );
        return;
      }
    } else if (
      canManageLifecycle &&
      agreement &&
      acquisitionType !== "consignment"
    ) {
      const endResult = await supabase
        .from("vehicle_consignment_agreements")
        .update({
          status: "ended",
          ends_on: new Date().toISOString().slice(0, 10),
        })
        .eq("id", agreement.id);
      if (endResult.error) {
        setSaving(false);
        setError(endResult.error.message);
        return;
      }
    }
    setSaving(false);
    onSaved();
  };
  return (
    <div className="stock-dialog-backdrop">
      <section className="stock-dialog" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">
              {vehicle ? "Ficha da viatura" : "Nova entrada"}
            </p>
            <h2>
              {vehicle
                ? `${vehicle.make} ${vehicle.model}`
                : "Adicionar viatura"}
            </h2>
            <p>
              Registe a origem, acompanhe a etapa física e controle o estado
              comercial sem perder histórico.
            </p>
          </div>
          <button className="stock-close" onClick={onClose}>
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <fieldset className="stock-section">
            <legend>Entrada e acompanhamento</legend>
            <p className="stock-section-help">
              A origem não muda o estado. A etapa indica onde a viatura está
              fisicamente no processo.
            </p>
            <div className="stock-form-grid">
              <label>
                <span>Tipo de entrada</span>
                <select
                  name="acquisition_type"
                  value={acquisitionType}
                  onChange={(e) => setAcquisitionType(e.target.value)}
                  disabled={!writable || (!!agreement && !canManageLifecycle)}
                >
                  {Object.entries(acquisitionLabels).map(([key, label]) => (
                    <option value={key} key={key} disabled={key === "consignment" && !canManageLifecycle && vehicle?.acquisition_type !== "consignment"}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Etapa operacional</span>
                <select
                  name="operational_stage"
                  defaultValue={vehicle?.operational_stage ?? "reception"}
                  disabled={!writable}
                >
                  {Object.entries(stageLabels).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Estado da viatura</span>
                <select
                  name="stock_status"
                  value={chosenStatus}
                  onChange={(e) => setChosenStatus(e.target.value)}
                  disabled={!writable}
                >
                  {Object.entries(stockLabels).map(([key, label]) => (
                    <option
                      value={key}
                      key={key}
                      disabled={
                        (saleStatuses.has(key) &&
                          key !== vehicle?.stock_status) ||
                        ((key === "paused" || key === "archived") &&
                          !canManageLifecycle)
                      }
                    >
                      {label}
                    </option>
                  ))}
                </select>
                <small>
                  Reservas e vendas serão alteradas pelos respetivos processos.
                </small>
              </label>
              <label>
                <span>Publicação após venda</span>
                <select
                  name="sold_publication_mode"
                  defaultValue={vehicle?.sold_publication_mode ?? "unpublish"}
                  disabled={!writable}
                >
                  <option value="unpublish">Retirar do site</option>
                  <option value="keep_published">
                    Manter no site com etiqueta “Vendida”
                  </option>
                </select>
              </label>
              {chosenStatus === "paused" && (
                <label className="wide">
                  <span>Motivo da pausa *</span>
                  <textarea
                    name="pause_reason"
                    rows={2}
                    defaultValue={vehicle?.pause_reason ?? ""}
                    required
                    disabled={!writable}
                  />
                </label>
              )}
            </div>
          </fieldset>
          {acquisitionType === "consignment" && (
            <fieldset className="stock-section consignment-section">
              <legend>Consignação</legend>
              <p className="stock-section-help">
                Identifique o proprietário e as condições acordadas. O mesmo
                proprietário pode ficar associado a várias viaturas.
              </p>
              {!canManageLifecycle && (
                <p className="consignment-permission-note">
                  Apenas o Master ou Gestor pode alterar condições de consignação.
                </p>
              )}
              {!consignmentReady ? (
                <p className="stock-section-help">A carregar o acordo…</p>
              ) : (
                <div className="stock-form-grid">
                  <label className="wide">
                    <span>Proprietário *</span>
                    <select
                      name="owner_id"
                      value={ownerId}
                      onChange={(e) => setOwnerId(e.target.value)}
                      disabled={!writable || !canManageLifecycle}
                    >
                      <option value="__new__">+ Cadastrar novo proprietário</option>
                      {owners.map((owner) => (
                        <option key={owner.id} value={owner.id}>
                          {owner.display_name}{owner.tax_id ? ` · ${owner.tax_id}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  {ownerId === "__new__" && (
                    <>
                      <label>
                        <span>Tipo de proprietário *</span>
                        <select name="owner_type" defaultValue="individual" disabled={!writable || !canManageLifecycle}>
                          <option value="individual">Pessoa singular</option>
                          <option value="company">Empresa</option>
                        </select>
                      </label>
                      <label>
                        <span>Nome do proprietário *</span>
                        <input name="owner_name" required disabled={!writable || !canManageLifecycle}/>
                      </label>
                      <label><span>NIF ou NIPC</span><input name="owner_tax_id" disabled={!writable || !canManageLifecycle}/></label>
                      <label><span>Telefone</span><input name="owner_phone" disabled={!writable || !canManageLifecycle}/></label>
                      <label><span>Email</span><input name="owner_email" type="email" disabled={!writable || !canManageLifecycle}/></label>
                      <label><span>Morada</span><input name="owner_address" disabled={!writable || !canManageLifecycle}/></label>
                      <label className="wide"><span>Notas do proprietário</span><textarea name="owner_notes" rows={2} disabled={!writable || !canManageLifecycle}/></label>
                    </>
                  )}
                  <label>
                    <span>Estado do acordo *</span>
                    <select name="agreement_status" defaultValue={agreement?.status ?? "active"} disabled={!writable || !canManageLifecycle}>
                      <option value="draft">Rascunho</option>
                      <option value="active">Ativo</option>
                      <option value="ended">Terminado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </label>
                  <label>
                    <span>Valor devido ao proprietário *</span>
                    <input name="owner_amount" type="number" min="0" step="0.01" required defaultValue={agreement ? agreement.owner_amount_cents / 100 : ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Forma de remuneração do stand</span>
                    <select name="commission_type" defaultValue={agreement?.commission_type ?? "margin"} disabled={!writable || !canManageLifecycle}>
                      <option value="margin">Margem entre valor devido e venda</option>
                      <option value="fixed">Comissão fixa</option>
                      <option value="percentage">Comissão percentual</option>
                    </select>
                  </label>
                  <label>
                    <span>Comissão fixa ou percentual</span>
                    <input name="commission_value" type="number" min="0" step="0.01" defaultValue={agreement?.commission_value ?? ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Preço mínimo autorizado</span>
                    <input name="minimum_sale_price" type="number" min="0" step="0.01" defaultValue={agreement?.minimum_sale_price_cents != null ? agreement.minimum_sale_price_cents / 100 : ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Data de início *</span>
                    <input name="starts_on" type="date" required defaultValue={agreement?.starts_on ?? new Date().toISOString().slice(0, 10)} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label>
                    <span>Data de fim</span>
                    <input name="ends_on" type="date" defaultValue={agreement?.ends_on ?? ""} disabled={!writable || !canManageLifecycle}/>
                  </label>
                  <label className="wide"><span>Condições acordadas</span><textarea name="terms" rows={3} defaultValue={agreement?.terms ?? ""} disabled={!writable || !canManageLifecycle}/></label>
                  <label className="wide"><span>Notas internas do acordo</span><textarea name="consignment_notes" rows={2} defaultValue={agreement?.internal_notes ?? ""} disabled={!writable || !canManageLifecycle}/></label>
                </div>
              )}
            </fieldset>
          )}
          <fieldset className="stock-section">
            <legend>Identificação</legend>
            <div className="stock-form-grid">
              <label>
                <span>Referência de stock *</span>
                <input
                  name="stock_number"
                  defaultValue={vehicle?.stock_number}
                  required
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Matrícula</span>
                <input
                  name="registration"
                  defaultValue={vehicle?.registration ?? ""}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Marca *</span>
                <input
                  name="make"
                  defaultValue={vehicle?.make}
                  required
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Modelo *</span>
                <input
                  name="model"
                  defaultValue={vehicle?.model}
                  required
                  disabled={!writable}
                />
              </label>
              <label className="wide">
                <span>Versão</span>
                <input
                  name="version"
                  defaultValue={vehicle?.version ?? ""}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Ano da primeira matrícula</span>
                <input
                  name="year"
                  type="number"
                  min="1900"
                  max="2100"
                  defaultValue={vehicle?.first_registration_year ?? ""}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Quilómetros</span>
                <input
                  name="mileage"
                  type="number"
                  min="0"
                  defaultValue={vehicle?.mileage_km ?? 0}
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Combustível</span>
                <select
                  name="fuel_type"
                  defaultValue={vehicle?.fuel_type ?? ""}
                  disabled={!writable}
                >
                  <option value="">Escolher</option>
                  {[
                    "Gasolina",
                    "Diesel",
                    "Híbrido",
                    "Híbrido plug-in",
                    "Elétrico",
                    "GPL",
                  ].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Transmissão</span>
                <select
                  name="transmission"
                  defaultValue={vehicle?.transmission ?? ""}
                  disabled={!writable}
                >
                  <option value="">Escolher</option>
                  <option>Manual</option>
                  <option>Automática</option>
                </select>
              </label>
              <label>
                <span>Cor</span>
                <input
                  name="color"
                  defaultValue={vehicle?.color ?? ""}
                  disabled={!writable}
                />
              </label>
            </div>
          </fieldset>
          <fieldset className="stock-section">
            <legend>Preço e publicação</legend>
            <div className="stock-form-grid">
              <label>
                <span>Preço de compra</span>
                <input
                  name="purchase_price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={
                    vehicle?.purchase_price_cents != null
                      ? vehicle.purchase_price_cents / 100
                      : ""
                  }
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Preço de venda</span>
                <input
                  name="sale_price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={
                    vehicle?.sale_price_cents != null
                      ? vehicle.sale_price_cents / 100
                      : ""
                  }
                  disabled={!writable}
                />
              </label>
              <label>
                <span>Estado da publicação</span>
                <select
                  name="publication_status"
                  defaultValue={vehicle?.publication_status ?? "draft"}
                  disabled={!writable}
                >
                  {Object.entries(publicationLabels).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="stock-check">
                <input
                  name="featured"
                  type="checkbox"
                  defaultChecked={vehicle?.featured}
                  disabled={!writable}
                />
                <span>Destacar esta viatura</span>
              </label>
              <label className="wide">
                <span>Descrição</span>
                <textarea
                  name="description"
                  rows={4}
                  defaultValue={vehicle?.description ?? ""}
                  disabled={!writable}
                />
              </label>
            </div>
          </fieldset>
          {error && <p className="stock-error">{error}</p>}
          <footer>
            <button type="button" className="stock-secondary" onClick={onClose}>
              {writable ? "Cancelar" : "Fechar"}
            </button>
            {writable && (
              <button className="stock-primary" disabled={saving}>
                {saving ? "A guardar…" : "Guardar viatura"}
              </button>
            )}
          </footer>
        </form>
      </section>
    </div>
  );
}
